import '../assets/entity-list.css';
import { useEffect, useState, useCallback } from "react";
import { DataTable, DataTablePageEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { createPager, MessageDto, Pager, SearchSessionsApiResponseDto, SessionSearchDto } from "../general/dtos";
import { Api } from '../general/api';
import { Dropdown } from 'primereact/dropdown';
import { createEmptyFilter, createEmptyOrderBy, DataTableFilter, DataTableOrderBy, getPaginatorLayout, getPaginatorOptions } from '../general/data-grid-utils';
import { GeneralErrorDialog } from './dialogs/error-dialog';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { AxiosError } from 'axios';
import { Divider } from 'primereact/divider';
import { Checkbox } from 'primereact/checkbox';
import Config from '../general/config';
import { GroomSessionsDialog } from './dialogs/groom-sessions-dialog';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { isObjectLiteralElement } from 'typescript';
import { useApi } from '../providers/api-provider';

interface ActivityListProps {
    selectedSession: SessionSearchDto | null;
    onSelectionChange: (session: SessionSearchDto) => void;
}

export function ActivityList({ selectedSession, onSelectionChange }: ActivityListProps) {
    const api: Api = useApi();

    const [loading, setLoading] = useState<boolean>(false);
    const [sessions, setSessions] = useState<SessionSearchDto[]>([]);
    const [excludeEmpty, setExcludeEmpty] = useState<boolean>(false);
    const [pager, setPager] = useState<Pager>(createPager(1, 10, 0));

    const [filters, setFilters] = useState<DataTableFilter>(createEmptyFilter());

    const [pendingFilterBy, setPendingFilterBy] = useState<string>("");
    const [pendingFilterValue, setPendingFilterValue] = useState<string>("");

    const [sorting, setSorting] = useState<DataTableOrderBy>(createEmptyOrderBy());

    const [error, setError] = useState<string>('');
    const [errorTitle, setErrorTitle] = useState<string>('');
    const [errorDetails, setErrorDetails] = useState<string>('');

    const [showGroomingDialog, setShowGroomingDialog] = useState<boolean>(false);
    const [showGroomingFinished, setShowGroomingFinished] = useState<boolean>(false);
    const [groomingMessage, setGroomingMessage] = useState<string>('');
    
    const fetchSessions = useCallback(async () => {
        if (loading) return;

        setLoading(true);
        try {
            const json: SearchSessionsApiResponseDto = 
                await api.searchSessions(pager.page, pager.pageSize, sorting.orderBy, sorting.orderDirection, filters.filterBy, filters.filterValue, excludeEmpty);

            setSessions(json.data);
            if (json.pager.page !== pager.page || json.pager.pageSize !== pager.pageSize || json.pager.totalPages !== pager.totalPages || json.pager.totalRows !== pager.totalRows) {
                setPager(json.pager);
            }
        } catch (error: AxiosError | any) {
            const message: string = error.response?.data?.message ?? 'Load error';
            setError(message);
            setErrorTitle('Error loading user session list');
            setErrorDetails('There was an unhandled error while trying to load user session list. Please try again later or contact support :)');
        }
        setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pager.page, pager.pageSize, sorting, filters, excludeEmpty]);

    useEffect(() => {
        fetchSessions();
        onSelectionChange(null!);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pager.page, pager.pageSize, sorting.orderBy, sorting.orderDirection, filters.filterBy, filters.filterValue, excludeEmpty]);

    const groomSessions = async (mode: string, days: number): Promise<void> => {
        try {
            const json: MessageDto = await api.groomSessions(days, mode);
            const newPager: Pager = { page: 1, pageSize: pager.pageSize, totalRows: 0, totalPages: 0};
            setShowGroomingFinished(true);
            setGroomingMessage(`${mode.charAt(0).toUpperCase() + mode.slice(1)} sessions older than ${days} days were deleted (${json.message}).`);
            setPager(newPager);
            onSelectionChange(null!);
        } catch (error: AxiosError | any) {
            const message: string = error.response?.data?.message ?? 'Grooming error';
            setError(message);
            setErrorTitle('Error groomingg user sessions');
            setErrorDetails('There was an unhandled error while trying to groom user sessions. Please try again later or contact support :)');
        }
    };

    const onPageChange = (e: DataTablePageEvent) => {
        let newPageSize: number | string = e.rows;
        const newPage: number = (e.page ?? 0) + 1; // safe default
        setPager({
            ...pager,
            page: newPage,
            pageSize: newPageSize
        });
    };

    const onSort = (e: any) => {
        setSorting({
            orderBy: e.sortField,
            orderDirection: e.sortOrder === 1 ? "asc" : "desc"
        });
    };

    const buildMailto = (session: SessionSearchDto) => {
            const displayName = (session.user_name ?? '').trim();
            const subject = encodeURIComponent(`Message from ${Config.app_name} Admin`);
            const toField = encodeURIComponent(`${displayName} <${session.email}>`);
            const body = encodeURIComponent(
                `Hi ${session.user_name ?? 'there'},
    
    I hope you're doing well.
    
    I wanted to reach out to you regarding ${session.module_name} ...
    
    Best regards,
    ${Config.app_name} Admin`
            );
    
            return `mailto:${toField}?subject=${subject}&body=${body}`;
        };

    const rowClassName = (row: SessionSearchDto) =>
        selectedSession?.session_id === row.session_id ? "row-selected" : "";

    const handleRowClick = (event: any) => {
        const row: SessionSearchDto = event.data;
        onSelectionChange(row);
    };

    const rowsPerPageOptions = getPaginatorOptions(pager, false);
    const paginatorLayout = getPaginatorLayout(pager);
    const isAllDataLoaded: boolean = sessions?.length >0 && sessions?.length >= pager.totalRows;

    return (<>
        <div className="entity-list-container">
            <div className="entity-list-toolbar admin-toolbar">
                <div className="filter-controls" style={{ visibility: isAllDataLoaded ? 'hidden' : 'visible' }}>
                    <span className="filter-title">Filter:</span>
                    <Dropdown
                        value={pendingFilterBy}
                         scrollHeight={'300px'}
                        options={[
                            { label: "None", value: "None" },
                            { label: "Name", value: "user_name" },
                            { label: "Email", value: "email" },
                            { label: "Module name", value: "module_name" },
                            { label: "Started", value: "started_at" },
                        ]}
                        onChange={(e) => {
                            const newField: string = e.value;
                            setPendingFilterBy(newField);   

                            if (newField === "None") {
                                setPendingFilterValue("");
                                setFilters({ filterBy: "", filterValue: "" });
                                return;
                            }
                        }}
                        placeholder="Field"
                        className="filter-field"
                    />

                    <InputText
                        value={pendingFilterValue}
                        onChange={(e) => setPendingFilterValue(e.target.value)}
                        placeholder="Search substring..."
                        className="filter-input"
                        disabled={pendingFilterBy === "None"}
                    />

                    <Button
                        label="Apply" className="p-button-text p-button-sm" disabled={pendingFilterBy === "None"}
                        onClick={async () => {
                            setFilters({ filterBy: pendingFilterBy, filterValue: pendingFilterValue });
                         }}
                    />
                    <Divider layout='vertical' />
                </div>
                <div className="filter-checkbox-wrapper">
                    <Checkbox title='Hide empty' checked={excludeEmpty} onChange={(e) => {
                            setExcludeEmpty(e.checked ?? false);
                        }} /><span className="text-primary">&nbsp;&nbsp;Hide empty</span>
                </div>
                <Divider layout='vertical' />
                <Button
                    label="Groom..." className="p-button-text p-button-sm" style={{ marginRight: '1rem' }}
                    onClick={() => setShowGroomingDialog(true) }
                />
            </div>

            <div className="entity-list-wrapper">
            <DataTable
                lazy={!isAllDataLoaded}
                filterDisplay={isAllDataLoaded ? "menu" : undefined}
                dataKey="session_id"
                selection={selectedSession}
                onRowClick={handleRowClick}
                loading={loading}

                selectionMode="single"
                
                value={sessions}
                paginator
                rows={pager.pageSize}
                totalRecords={pager.totalRows}
                first={(pager.page - 1) * pager.pageSize}
                onPage={onPageChange}
                sortField={sorting.orderBy}
                sortOrder={sorting.orderDirection === "asc" ? 1 : -1}
                onSort={isAllDataLoaded ? undefined : onSort}
                removableSort
                scrollable
                scrollHeight="flex"
                rowClassName={rowClassName}
                paginatorTemplate={paginatorLayout}
                rowsPerPageOptions={rowsPerPageOptions}
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                emptyMessage={<div className="flex align-items-center gap-2"><i className="pi pi-info-circle" /><span>The user session list is empty.</span></div>}
            >
                <Column field="user_name" header="User name" sortable filter={isAllDataLoaded}/>
                <Column field="email" header="Email" sortable filter={isAllDataLoaded}
                    body={(row: SessionSearchDto) => (
                    <a
                        href={buildMailto(row)}
                        onClick={(e) => e.stopPropagation()} // don’t trigger row selection
                        style={{ color: "#0078d4", textDecoration: "underline", cursor: "pointer" }}
                        title={`Email ${row.user_name}`}
                    >
                        {row.email}
                    </a>
                )}/>
                <Column field="module_name" header="Module" sortable filter={isAllDataLoaded} />
                <Column field="started_at" header="Started" sortable  filter={isAllDataLoaded} />
                <Column field="event_count" header="Events"/>
            </DataTable>
            </div>
        </div>

        <GeneralErrorDialog
            visible={error.length > 0}
            title={errorTitle}
            message={error}
            details={errorDetails}
            onClose={() => setError('')}
        />

        <GroomSessionsDialog
            visible={showGroomingDialog}
            onDeleted={async (options) => await groomSessions(options.mode, options.days)}
            onClose={() => setShowGroomingDialog(false)}
        />

        <ConfirmDialog 
            visible={showGroomingFinished}
            icon='pi pi-info-circle'
            header='Grooming succeeded'
            message={groomingMessage}
            acceptLabel='OK'
            rejectLabel=' '
            accept={() => { setShowGroomingFinished(false); }}
            onHide={() => { setShowGroomingFinished(false); }}
            reject={null!}
        />
    </>);
}